import { DatePipe } from '@angular/common'
import { Pipe, type PipeTransform } from '@angular/core'
import { toDate } from 'date-fns-tz'
import { getAccountTimezone } from '@awork/_shared/functions/account-timezone'
import { convertToTimezone } from '@awork/_shared/functions/date-operations'
import getLanguage from '@awork/_shared/functions/get-language'

/** Formats instants in an IANA timezone, using the saved account setting by default. */
@Pipe({ name: 'zonedDate', pure: false })
export class ZonedDatePipe implements PipeTransform {
  /**
   * Formats an instant with Angular's locale formats without device DST adjustments.
   * @param value The instant to display.
   * @param [format='mediumDate'] An Angular date format.
   * @param [timezone] An explicit view timezone; otherwise the account timezone.
   * @param [locale] The display language.
   * @returns The formatted date, or null for an empty value.
   */
  transform(
    value: Date | string | number | null | undefined,
    format = 'mediumDate',
    timezone?: string,
    locale = getLanguage()
  ): string | null {
    if (value === null || value === undefined || value === '') {
      return null
    }

    const instant = toDate(value)

    if (Number.isNaN(instant.getTime())) {
      throw new RangeError('Invalid time value')
    }

    const zonedDate = convertToTimezone(instant, timezone || getAccountTimezone())
    const offsetMinutes = (instant.getTime() - zonedDate.getTime()) / 60_000

    // UTCDate keeps clock fields out of device DST gaps. Supply the target offset for timezone tokens.
    zonedDate.getTimezoneOffset = () => offsetMinutes

    return new DatePipe(locale).transform(zonedDate, format, undefined, locale)
  }
}
